import React from 'react';
import { Tooltip as RechartsTooltip, TooltipProps } from 'recharts';
import { Box, Span, Text, TextProps } from '@chakra-ui/react';
import {
  ColorScheme,
  CustomTooltipProps,
  TooltipTitle,
  TooltipItem,
} from './interface';

export interface ChartTooltipConfig {
  enabled?: boolean;
  cursor?: boolean | object;
  customContent?: (props: CustomTooltipProps) => React.ReactNode;
  title?: TooltipTitle;
  items?: TooltipItem[];
}

export interface ChartTooltipProps {
  cursor?: boolean | object;
  tooltip?: ChartTooltipConfig;
  colorScheme: ColorScheme;
  data?: any[];
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  tooltip,
  cursor,
  colorScheme,
}) => {
  const isTooltipEnabled = tooltip?.enabled !== false;

  if (!isTooltipEnabled) {
    return null;
  }

  const defaultTooltipProps: TooltipProps<any, any> = {
    cursor: tooltip?.cursor ?? cursor ?? false,
    content: tooltip?.customContent
      ? (props: any) => {
          return tooltip.customContent?.({
            active: props.active,
            payload: props.payload,
            label: props.label,
            coordinate: props.coordinate,
            viewBox: props.viewBox,
            title: tooltip.title,
            items: tooltip.items,
          });
        }
      : (props: any) => {
          const { active, payload, label } = props;
          if (!active || !payload?.length) return null;

          const resolveFromPayload = (key?: string): any => {
            if (!key) return '';
            for (const entry of payload) {
              if (entry?.payload && key in entry.payload)
                return (entry.payload as any)[key];
              if (key in entry) return (entry as any)[key];
            }
            return '';
          };

          const tooltipBox = (
            <Box
              color={colorScheme.tooltip.color}
              bg={colorScheme.tooltip.bg}
              border={`1px solid ${colorScheme.tooltip.border}`}
              borderRadius="md"
              p={4}
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.4)"
            >
              {tooltip?.title && (
                <Text
                  textStyle="chart.subtitle"
                  justifyContent="space-between"
                  display="flex"
                  width="100%"
                  mb={3}
                  {...(tooltip.title.labelProps || {})}
                >
                  {tooltip.title.label}:{' '}
                  <Span
                    ml={5}
                    color={colorScheme.tooltip.titleColor}
                    {...(tooltip.title.valueProps || {})}
                  >
                    {tooltip.title.valueKey
                      ? resolveFromPayload(tooltip.title.valueKey)
                      : (tooltip.title.value ?? '')}
                  </Span>
                </Text>
              )}
              {tooltip?.items?.map((item, index) => (
                <Text
                  key={index}
                  textStyle="chart.value"
                  display="flex"
                  width="100%"
                  justifyContent="space-between"
                  {...(item.labelProps || {})}
                >
                  {item.label}:{' '}
                  <Span
                    ml={5}
                    color={colorScheme.tooltip.valueColor}
                    {...(item.valueProps || {})}
                  >
                    {item.valueKey
                      ? resolveFromPayload(item.valueKey)
                      : (item.value ?? '')}
                  </Span>
                </Text>
              ))}
              {!tooltip?.title && !tooltip?.items && (
                <>
                  <Text textStyle="chart.title" mb={3}>
                    <Span color={colorScheme.tooltip.titleColor}>{label}</Span>
                  </Text>
                  {payload.map((item: any, index: number) => (
                    <Text key={index} textStyle="chart.subtitle">
                      {item.name || 'Value'}:{' '}
                      <Span color={colorScheme.tooltip.valueColor}>
                        {item.value}
                      </Span>
                    </Text>
                  ))}
                </>
              )}
            </Box>
          );
          return tooltipBox;
        },
  };

  return <RechartsTooltip {...defaultTooltipProps} />;
};
